/* global fbq */
import React from "react";
import PropTypes from "prop-types";
import { Link, Redirect } from "react-router-dom";
import classNames from "classnames";

import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";

import commons from "../common/commons";
import {
  EuropeanStockBadge,
  EuropeanStockEta,
  EuropeanStockNotice,
  europeanStockCardStyles,
} from "../common/EuropeanStockMark";
import BrandLogoImage from "../common/BrandLogoImage";
import LimitedOfferCountdown from "../common/LimitedOfferCountdown";
import { formatIls } from "./tirePairModel";
import { formatTireSizeLabel, parseSetAxleSizes, readProductScore } from "./tirePairDisplay";
import { getTireAvailabilityDetails } from "./tireAvailabilityDetails";
import tireCardStyles, { STOCK_TONE } from "./tireCardStyles";
import {
  AvailabilityBadge,
  AxleRow,
  ScoreMark,
  productPath,
} from "./TireCardParts";

const CARD_SIZE_SEPARATOR = " · ";

function specRows(product, isSet) {
  const plain = { showBadge: false, score: null, price: null };

  if (!isSet) {
    return [{
      key: "size",
      label: "מידה",
      size: formatTireSizeLabel(
        product.displaySize,
        product.load || product.loadIndex,
        product.speed || product.speedIndex,
        CARD_SIZE_SEPARATOR
      ),
      ...plain,
    }];
  }

  const parsed = parseSetAxleSizes(product.displaySize);
  if (!parsed) {
    return [{
      key: "size",
      label: null,
      size: formatTireSizeLabel(product.displaySize, product.load, product.speed, CARD_SIZE_SEPARATOR),
      ...plain,
    }];
  }

  return [
    {
      key: "front",
      label: "קדמי",
      size: formatTireSizeLabel(parsed.front, product.frontLoad, product.frontSpeed, CARD_SIZE_SEPARATOR),
      ...plain,
    },
    {
      key: "rear",
      label: "אחורי",
      size: formatTireSizeLabel(parsed.rear, product.rearLoad, product.rearSpeed, CARD_SIZE_SEPARATOR),
      ...plain,
    },
  ];
}

function hasLiveCountdown(limitedOffer) {
  if (!limitedOffer || !limitedOffer.expiresAt) {
    return false;
  }
  return new Date(limitedOffer.expiresAt).getTime() > 0;
}

const styles = (theme) => {
  const base = tireCardStyles(theme);
  return {
  ...base,
  // Fixed media box: every photo is fitted into the same 176x176 frame,
  // whatever its natural size, so cards line up at the same height.
  media: {
    ...base.media,
    alignItems: "center",
    justifyContent: "center",
    width: 200,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: 160,
    },
  },
  imageBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    width: 176,
    height: 176,
    padding: theme.spacing(1),
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#fff",
    [theme.breakpoints.down("sm")]: {
      width: 136,
      height: 136,
    },
  },
  productImage: {
    display: "block",
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
  },
  // Top-aligned, stacked: logo → title → pills → specs.
  details: {
    ...base.details,
    justifyContent: "flex-start",
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  brandLine: {
    display: "flex",
    alignItems: "center",
    minHeight: 28,
    marginBottom: theme.spacing(0.5),
  },
  brandLogoSmall: {
    display: "block",
    height: 24,
    width: "auto",
    maxWidth: 120,
    objectFit: "contain",
  },
  specs: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  axleRow: {
    ...base.axleRow,
    minHeight: 32,
  },
  availabilityLines: {
    marginTop: theme.spacing(1),
  },
  arrivalPill: {
    color: STOCK_TONE.eu.color,
    backgroundColor: STOCK_TONE.eu.background,
    marginTop: theme.spacing(0.75),
  },
  infoTrigger: {
    display: "inline-flex",
    verticalAlign: "middle",
    marginLeft: theme.spacing(0.5),
    color: STOCK_TONE.eu.color,
    cursor: "help",
    borderRadius: "50%",
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  infoIcon: {
    fontSize: 16,
  },
  offerChipLive: {
    backgroundColor: "#d32f2f",
    color: "#fff",
  },
  paidShipping: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  ...europeanStockCardStyles(),
  };
};

class TireListingCard extends React.Component {
  ordering = false;

  state = {
    ordering: false,
    redirectToCart: false,
  };

  orderNow = async () => {
    if (this.ordering || this.state.ordering) {
      return;
    }

    const { product, addProductsToCart, collectMetric, metricSource } = this.props;
    const ean = product.ean;
    const price = product.price;
    this.ordering = true;
    this.setState({ ordering: true });

    try {
      if (typeof fbq === "function") {
        fbq("track", "AddToCart", {
          content_ids: [ean],
          content_type: "product",
          value: price,
          currency: "ILS",
        });
      }

      collectMetric({
        kind: "PRODUCT_ADD_TO_CART",
        source: metricSource,
        ean,
      });

      const added = await addProductsToCart([{
        ean,
        isSet: !!product.set,
        isAtvSet: !!product.atvSet,
      }]);

      if (!added) {
        this.ordering = false;
        this.setState({ ordering: false });
        return;
      }

      this.setState({ redirectToCart: true });
    } catch (error) {
      this.ordering = false;
      this.setState({ ordering: false });
      return error;
    }
  };

  renderInfoTooltip(text, className) {
    const { classes } = this.props;
    if (!text) {
      return null;
    }

    return (
      <Tooltip title={text} arrow enterTouchDelay={0} leaveTouchDelay={6000}>
        <span
          className={classNames(classes.infoTrigger, className)}
          tabIndex={0}
          role="img"
          aria-label={text}
        >
          <InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
        </span>
      </Tooltip>
    );
  }

  render() {
    const { classes, product, limitedOffer, availabilityContext } = this.props;
    const { ordering, redirectToCart } = this.state;

    if (redirectToCart) {
      return <Redirect to="/cart" />;
    }

    const isSet = !!product.set;
    const title = product.displayName || product.name || "";
    const brand = product.brand || "";
    const path = productPath(product);
    const details = getTireAvailabilityDetails(product, availabilityContext);
    const score = readProductScore(product);
    const rows = specRows(product, isSet);
    const image = commons.getProductPrimaryImage(
      product.cdn,
      product.ean,
      product.source === "georgia"
    );
    const imageUrl = image && image.url;
    const orderLabel = isSet ? "הוסף סט לסל" : "הוסף לסל";
    const showAvailability = details.lines.length > 0 || details.arrivalLabel;
    const isEu = details.kind === "eu";
    const imageNode = (
      <img
        className={classes.productImage}
        src={imageUrl || ""}
        alt={`${brand} ${title} ${product.displaySize || ""}`.trim()}
        loading="lazy"
      />
    );

    return (
      <article className={classNames(classes.card, isEu && classes.euCard)} dir="rtl" aria-label={title}>
        <div className={classes.media}>
          {path ? (
            <Link to={path} className={classes.imageBox} tabIndex={-1} aria-hidden="true">
              {imageNode}
            </Link>
          ) : (
            <div className={classes.imageBox}>{imageNode}</div>
          )}
        </div>
        <div className={classes.details}>
          {brand ? (
            <div className={classes.brandLine}>
              <BrandLogoImage
                brand={brand}
                alt={brand}
                className={classes.brandLogoSmall}
                fallback={(
                  <Typography variant="overline" component="span" className={classes.brandFallback}>
                    {brand}
                  </Typography>
                )}
              />
            </div>
          ) : null}
          <div>
            <div>
              <Typography variant="h6" component="h2" className={classes.title}>
                {path ? (
                  <Link to={path} className={classes.titleLink}>
                    {title}
                  </Link>
                ) : title}
              </Typography>
              <div className={classes.meta}>
                <Typography
                  variant="caption"
                  component="span"
                  className={classNames(
                    classes.kindChip,
                    classes.metaItem,
                    isSet ? classes.kindChipSet : classes.kindChipPair
                  )}
                >
                  {isSet ? "סט מקורי" : "צמיג בודד"}
                </Typography>
                {isEu ? (
                  <EuropeanStockBadge label={details.label} className={classes.metaItem} />
                ) : (
                  <AvailabilityBadge
                    classes={classes}
                    badge={{ kind: details.kind, label: details.label }}
                    className={classes.metaItem}
                  />
                )}
                {score != null ? (
                  <ScoreMark classes={classes} score={score} className={classes.metaItem} />
                ) : null}
                {hasLiveCountdown(limitedOffer) ? (
                  <span className={classNames(classes.kindChip, classes.offerChipLive, classes.metaItem)}>
                    <LimitedOfferCountdown inline expiresAt={limitedOffer.expiresAt} />
                  </span>
                ) : null}
                {limitedOffer && !hasLiveCountdown(limitedOffer) ? (
                  <Typography
                    variant="caption"
                    component="span"
                    className={classNames(classes.offerChip, classes.metaItem)}
                  >
                    מבצע לזמן מוגבל
                  </Typography>
                ) : null}
              </div>
            </div>
          </div>
          {rows.length > 0 ? (
            <div className={classes.specs}>
              {rows.map((row) => (
                <AxleRow key={row.key} classes={classes} row={row} />
              ))}
            </div>
          ) : null}
          {isEu ? <EuropeanStockNotice details={details} /> : null}
          {!isEu && showAvailability ? (
            <div className={classes.availabilityLines}>
              {details.lines.map((line, index) => (
                <Typography
                  key={`${index}-${line}`}
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {line}
                  {index === 0 ? this.renderInfoTooltip(details.tooltip) : null}
                </Typography>
              ))}
              {details.arrivalLabel ? (
                <Typography
                  variant="caption"
                  component="span"
                  className={classNames(classes.kindChip, classes.arrivalPill)}
                >
                  {details.arrivalLabel}
                </Typography>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className={classes.cta}>
          <Typography variant="overline" component="div" className={classes.priceLabel}>
            {isSet ? "מחיר לסט (2 צמיגים)" : "מחיר לצמיג"}
          </Typography>
          <Typography variant="h5" component="p" className={classes.totalPrice}>
            <bdi dir="ltr">{formatIls(product.price)}</bdi>
          </Typography>
          {product.free_shipping ? (
            <div className={classes.shipping}>
              <LocalShippingIcon className={classes.shippingIcon} fontSize="small" aria-hidden="true" />
              <Typography variant="body2" component="span" className={classes.shippingText}>
                משלוח חינם
              </Typography>
            </div>
          ) : (
            <Typography variant="caption" className={classes.paidShipping}>
              משלוח: ₪60
            </Typography>
          )}
          {isEu ? <EuropeanStockEta label={details.arrivalLabel} /> : null}
          <Button
            type="button"
            variant="contained"
            color="primary"
            fullWidth
            disableElevation
            className={classes.orderButton}
            disabled={ordering}
            onClick={this.orderNow}
            aria-label={`${orderLabel}: ${title}`}
            aria-busy={ordering}
          >
            {ordering ? <CircularProgress size={20} color="inherit" /> : orderLabel}
          </Button>
          {path ? (
            <Button
              component={Link}
              to={path}
              color="primary"
              fullWidth
              className={classes.detailsButton}
              aria-label={title ? `פרטים נוספים על ${title}` : "פרטים נוספים"}
            >
              פרטים נוספים
            </Button>
          ) : null}
        </div>
      </article>
    );
  }
}

TireListingCard.propTypes = {
  classes: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  limitedOffer: PropTypes.object,
  availabilityContext: PropTypes.shape({
    currentShipment: PropTypes.string,
    nextShipment: PropTypes.string,
    euContainerMode: PropTypes.object,
  }),
  addProductsToCart: PropTypes.func.isRequired,
  collectMetric: PropTypes.func.isRequired,
  metricSource: PropTypes.string.isRequired,
};

TireListingCard.defaultProps = {
  limitedOffer: null,
  availabilityContext: {},
};

export default withStyles(styles)(TireListingCard);
